/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AdcModel, ShuttleModel } from '../../models';
import { DrawerService, LanguageService, LogService, UtilsService } from '../../../../../core/services';
import { DropdownModel } from '../../../../../core/models';
import { FormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import { VendorEnum } from '../../enums';

@Injectable()
export class AdcsUtilsService {
    constructor(
        private drawerService: DrawerService,
        private languageService: LanguageService,
        private logService: LogService,
        private utilsService: UtilsService
    ) {
    }

    /**
     * Get image src based on running status.
     * @param row
     */
    public getShuttleRunningStatusSrc(row: any): string {
        let statusName: string = 'pending';
        if (row.adoptionStatus !== 'errorConnectionTimeout') {
            switch (row.status) {
                case 'online':
                    statusName = 'connected';
                    break;
                case 'offline':
                    statusName = 'disconnected';
                    break;
                default:
                    statusName = 'pending';
            }
        }

        return `./assets/centralisedManagement/icons/running-status-${statusName}.svg`;
    }

    /**
     * Set colour for ADC status.
     * @param row
     */
    public getShuttleStatusClassName(row: ShuttleModel): string {
        let statusName: string = 'pending';
        if (row.adoptionStatus !== 'errorConnectionTimeout') {
            switch (row.status) {
                case 'online':
                    statusName = 'online';
                    break;
                case 'offline':
                    statusName = 'disconnected';
                    break;
                default:
                    statusName = 'pending';
            }
        }

        return statusName;
    }

    /**
     * Guess product name by vendor id.
     * @param vendorId
     */
    public getProductNameFromVendor(vendorId: number): string {
        const productName = {
            0: 'Generic',
            1: 'BIG-IP',
            2: 'LoadMaster',
            3: 'Thunder',
            4: 'Netscaler',
            5: 'Enterprise',
            6: 'Endurance',
            7: 'Edgenexus'
        };

        return productName[vendorId] || 'no data';
    }

    /**
     * Get vendor title for span.
     * @param vendorId
     * @param suffix
     */
    public getVendorTitle(vendorId: number | string, suffix: string = 'ADC'): string {
        if (typeof vendorId === 'string') {
            return vendorId;
        }

        const vendor = {
            0: 'Generic',
            1: 'F5',
            2: 'Kemp',
            3: 'A10',
            4: 'Citrix',
            5: 'Loadbalancer',
            6: 'Loadbalancer',
            7: 'Edgenexus'
        };

        return (vendor[vendorId] + ' ' + suffix || 'no data');
    }

    /**
     * Get vendor name for image.
     * @param vendorId
     */
    public getVendorIconName(vendorId: number | string): string {
        if (typeof vendorId === 'string') {
            return vendorId;
        }

        const vendor = {
            0: 'brand-generic',
            1: 'brand-f5',
            2: 'brand-kemp',
            3: 'brand-a-10',
            4: 'brand-citrix',
            5: 'brand-lb',
            6: 'brand-lb',
            7: 'brand-edgenexus'
        };

        return vendor[vendorId];
    }

    /**
     * Remove starting v and last dot from version number.
     * @param version
     */
    public formatVersionNumber(version: string): string {
        return version.replace(/^v|\.$/g, '');
    }

    /**
     * Version can update
     * @param row
     */
    public versionCanUpdate(row: any): boolean {
        if (row.vendor === VendorEnum.LBENTERPRISE && row.updateStatus === 'available') {
            const cleanVersion: string = row.version?.startsWith('v') ? row.version.slice(1) : row.version;

            return this.utilsService.compareDecimals(cleanVersion, '8.13.0', '>=');
        } else {
            return false;
        }
    }

    /**
     * Get severity score class.
     * @param severityScore
     */
    public getSeverityScoreClass(severityScore: number): string {
        if (severityScore > 8.9) {
            return 'critical';
        } else if (severityScore > 6.9) {
            return 'high';
        } else if (severityScore > 3.9) {
            return 'medium';
        } else {
            return 'low';
        }
    }

    /**
     * Get recommendation text.
     * @param operationalRisk
     */
    public getRecommendationText(operationalRisk: string): string {
        switch (operationalRisk?.toLowerCase()) {
            case 'critical':
                return 'Upgrade to the latest version immediately and apply any available patches.';
            case 'high':
                return 'Apply the latest security patches and consider additional mitigations.';
            case 'medium':
                return 'Apply relevant security patches and follow best practices to minimize risks.';
            case 'low':
                return 'Monitor the situation and apply necessary updates or changes if deemed appropriate.';
            default:
                return 'No specific remediation recommendation available.';
        }
    }

    /**
     * Lock the form if lock is checked.
     * @param formGroup
     */
    public lockForm(formGroup: FormGroup): void {
        const locked: any = formGroup.get('locked').value;
        for (const control in formGroup.controls) {
            if (formGroup.controls.hasOwnProperty(control) && control !== 'locked') {
                if (locked) {
                    formGroup.controls[control].disable();
                } else {
                    formGroup.controls[control].enable();
                }
            }
        }
    }

    /**
     * Set tag value in form.
     * @param formGroup
     * @param event
     */
    public setTagsFormValue(formGroup: FormGroup, event: any[]): void {
        formGroup.get('tags').setValue(event);
    }

    /**
     * Get namespace friendly name by id.
     * @param namespaces
     * @param id
     */
    public getNamespaceFriendlyName(namespaces: any[], id: number): any {
        if (namespaces?.length) {
            const namespace = namespaces.find(item => item.key === id);

            return namespace?.value ? namespace.value : 'unknown';
        }
    }

    /**
     * Get the namespaces dropdown content
     * @param namespaces
     */
    public getNamespaceDropdownContent(namespaces: any[]): DropdownModel[] {
        const namespaceFilter: DropdownModel[] = namespaces.filter(item => item.key !== 0);

        return this.utilsService.convertArrayToDropDown(namespaceFilter, 'key', 'value');
    }

    /**
     * Get formatted shuttle dropdown (includes the subnet/mask as part of the label).
     * @param shuttleList
     */
    public getFormattedShuttleDropdown(shuttleList: ShuttleModel[]): DropdownModel[] {
        const dropdown: any[] = [];
        for (const shuttle of shuttleList) {
            let network = shuttle.ipAddress;
            if (shuttle.ipAddress && shuttle.network) {
                const suffix: string = !shuttle.legacy && shuttle.adoptionStatus === 'pending'
                    ? ` (${this.languageService.translate('pending')})`
                    : '';
                const netAddress: string = !shuttle.legacy && shuttle.adoptionStatus !== 'pending'
                    ? ` (${shuttle.network})` : '';

                network = suffix + netAddress;
            }

            dropdown.push(
                <DropdownModel>{
                    key: shuttle.id,
                    value: shuttle.label + ': ' + network,
                    options: { disabled: (shuttle.legacy || shuttle.adoptionStatus === 'pending' || shuttle.status === 'offline') }
                }
            );
        }

        return dropdown;
    }

    /**
     * Validate ip and port for duplicates on the same shuttle.
     * @param adcsList
     * @param formGroup
     * @param ipAddress
     * @param port
     * @param shuttleId
     * @private
     */
    public validatePortAndIpForSelectedShuttle(
        adcsList: AdcModel[], formGroup: FormGroup, ipAddress: string, port: number, shuttleId: any
    ): void {
        const foundMatch = adcsList.find(
            adc => adc.ipAddress === ipAddress && adc.port === port.toString() && adc.sidecarID === shuttleId
        );

        const ipAddressField = formGroup.get('ipAddress');
        const portField = formGroup.get('port');

        if (foundMatch) {
            ipAddressField.markAsTouched();
            portField.markAsTouched();

            ipAddressField.setErrors(
                { 'ipAddressField': `${this.languageService.translate('adc_duplicate_ip_message')}` }
            );
            portField.setErrors(
                { 'portField': `${this.languageService.translate('adc_port_clashes_with_another_ADC')}` }
            );
        } else if (ipAddressField.hasError('ipAddressField') || portField.hasError('portField')) {
            ipAddressField.setErrors({ 'ipAddressField': null });
            portField.setErrors({ 'portField': null });

            ipAddressField.updateValueAndValidity({ emitEvent: false });
            portField.updateValueAndValidity({ emitEvent: false });
        }
    }

    /**
     * Validate IP in subnet
     * @param shuttleList
     * @param form
     * @param ipAddress
     * @param shuttleId
     */
    public validateIPinSubnet(shuttleList: ShuttleModel[], form: FormGroup, ipAddress: string, shuttleId: number): void {
        if (ipAddress && shuttleId) {
            const selectedShuttle = shuttleList.find(item => item.id === shuttleId);
            const inRange = [];
            for (const subnetObject of selectedShuttle.subnets) {
                inRange.push(this.ipInSubnet(ipAddress, subnetObject.subnet));
            }

            const isInRange = inRange.some(item => item === true);
            const ipAddressField = form.get('ipAddress');
            if (!isInRange) {
                ipAddressField.setErrors(
                    { 'ipAddressField': `${this.languageService.translate('adc_ip_out_of_range')}` }
                );
                ipAddressField.markAsTouched();
            } else {
                ipAddressField.setErrors({ 'ipAddressField': null });
                ipAddressField.updateValueAndValidity({ emitEvent: false });
            }
        }
    }

    /**
     * Check if IP is subnet.
     * @param ip
     * @param subnet
     * Example usage:
     *      const ipAddress = '192.168.64.100';
     *      const subnet = '192.168.64.0/24';
     */
    public ipInSubnet(ip: string, subnet: string): boolean {
        // Split the subnet into the base IP and the mask
        const [subnetBase, subnetBits] = subnet.split('/');
        // eslint-disable-next-line no-bitwise
        const mask = ~(2 ** (32 - parseInt(subnetBits, 10)) - 1) >>> 0; // Create the mask

        // Convert IPs to 32-bit numbers
        const subnetBaseLong = this.ipToLong(subnetBase);
        const ipLong = this.ipToLong(ip);

        // Check if the IP address is in the subnet
        // eslint-disable-next-line no-bitwise
        return (ipLong & mask) === (subnetBaseLong & mask);
    }

    /**
     * Function to convert IP address to a 32-bit number
     * @param ip
     * @private
     */
    private ipToLong(ip: string): number {
        // eslint-disable-next-line no-bitwise
        return ip.split('.').reduce((acc, octet) => {
            // eslint-disable-next-line no-bitwise
            return (acc << 8) + parseInt(octet, 10);
        }, 0) >>> 0; // Use unsigned right shift to get a 32-bit unsigned integer
    }

    /**
     * Data has changed.
     * @param storedData
     * @param newData
     * @param propertiesToIgnore
     */
    public dataHasChanged(storedData: any, newData: AdcModel | ShuttleModel, propertiesToIgnore?: string[]): boolean {
        const cleanStoredData = propertiesToIgnore.length
            ? this.utilsService.deletePropertiesFromObject(storedData, propertiesToIgnore)
            : storedData;

        const dataClone = this.utilsService.duplicateObject(newData);
        const cleanResponseData = propertiesToIgnore.length
            ? this.utilsService.deletePropertiesFromObject(dataClone, propertiesToIgnore)
            : dataClone;

        if (JSON.stringify(cleanStoredData) !== JSON.stringify(cleanResponseData)) {
            const diff = this.utilsService.findDifference(cleanStoredData, cleanResponseData);
            this.logService.debug('AdcsUtilsService: dataHasChanged', 'The socket data and stored data don\'t match: - ' + diff);
        }

        return JSON.stringify(cleanStoredData) !== JSON.stringify(cleanResponseData);
    }

    /**
     * Update the drawer content if the response ID matches the template content ID.
     * @param templateContent
     * @param response
     * @param tableRows
     */
    public updateTemplateContent(
        templateContent: any,
        response: { data: AdcModel | ShuttleModel },
        tableRows: any
    ): any {
        if (templateContent && (templateContent.id === response.data.id)) {
            const matchedTableRows = tableRows.find((item: any) => item.id === response.data.id);
            if (matchedTableRows) {
                return matchedTableRows;
            } else {
                this.logService.debug(
                    'updateTemplateContent',
                    'Websocket data changed and the drawer is open but couldn\'t match data'
                );
            }
        }
    }

    /**
     * Check local storage for drawer content.
     * @param tableRows
     * @param callBack
     */
    public checkLocalStorageForDrawerContent(tableRows: any[], callBack: (...args: any[]) => void): any {
        const drawerState = JSON.parse(localStorage.getItem('drawerState'));
        const drawerVisibility = this.utilsService.getObjectValue(drawerState, `${location.pathname}.visibility`);

        if (tableRows.length && drawerVisibility) {
            const row = tableRows.find(item => item.id === drawerState[location.pathname].id);

            if (row?.id) {
                callBack(row);
            } else {
                this.drawerService.close();
            }
        }
    }
}
